import React, { useState, useEffect, useRef } from 'react';
import { profileAPI, uploadAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Phone, MapPin, Shield, FileText, Save, Loader2, Upload, Camera, CheckSquare, Square, Download, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ImageViewer } from '../../components/ui/image-viewer';
import { UploadChooser } from '../../components/ui/upload-chooser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [sameAddress, setSameAddress] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const photoRef = useRef(null);
  const aadhaarFrontRef = useRef(null);
  const aadhaarBackRef = useRef(null);
  const panRef = useRef(null);

  const [form, setForm] = useState({
    phone: '', mumbai_address: '', permanent_address: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
    aadhaar_card: '', aadhaar_front: '', aadhaar_back: '', pan_card: '', pan_url: '', profile_photo: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await profileAPI.get();
        const p = res.data;
        setProfile(p);
        const f = {
          phone: p.phone || '', mumbai_address: p.mumbai_address || '',
          permanent_address: p.permanent_address || '',
          emergency_contact_name: p.emergency_contact_name || '',
          emergency_contact_phone: p.emergency_contact_phone || '',
          emergency_contact_relation: p.emergency_contact_relation || '',
          aadhaar_card: p.aadhaar_card || '', aadhaar_front: p.aadhaar_front || '',
          aadhaar_back: p.aadhaar_back || '', pan_card: p.pan_card || '', pan_url: p.pan_url || '',
          profile_photo: p.profile_photo || '',
        };
        setForm(f);
        if (f.mumbai_address && f.permanent_address && f.mumbai_address === f.permanent_address) {
          setSameAddress(true);
        }
      } catch (e) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await profileAPI.update(form);
      setMessage(res.data.message || 'Profile updated');
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, field) => {
    try {
      const res = field === 'profile_photo'
        ? await uploadAPI.uploadPhoto(file)
        : await uploadAPI.uploadDocument(file);
      setForm((prev) => ({ ...prev, [field]: res.data.url }));
    } catch (e) {
      setMessage('Upload failed');
    }
  };

  const set = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => {
      const updated = { ...prev, [field]: val };
      if (field === 'mumbai_address' && sameAddress) {
        updated.permanent_address = val;
      }
      return updated;
    });
  };

  const toggleSameAddress = () => {
    const next = !sameAddress;
    setSameAddress(next);
    if (next) {
      setForm((prev) => ({ ...prev, permanent_address: prev.mumbai_address }));
    }
  };

  const getUrl = (p) => p ? (p.startsWith('http') ? p : `${BACKEND_URL}${p}`) : null;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#1D35A0]" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="my-profile-page">
      <button onClick={() => navigate('/portal')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1D35A0] transition-colors mb-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </button>
      <h2 className="text-xl font-bold text-[#1D35A0]">My Profile</h2>

      {/* Header with photo upload */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <UploadChooser onFile={(file) => handleFileUpload(file, 'profile_photo')} accept="image/*" testId="profile-photo-upload">
              <div className="relative group cursor-pointer">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1D35A0] to-[#F5AB00] flex items-center justify-center text-white text-lg font-bold overflow-hidden flex-shrink-0">
                  {getUrl(form.profile_photo)
                    ? <img src={getUrl(form.profile_photo)} alt="" className="w-full h-full object-cover" />
                    : profile?.full_name?.split(' ').map(n => n[0]).join('').substring(0, 2)
                  }
                </div>
                <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
            </UploadChooser>
            <div>
              <p className="text-lg font-semibold text-[#1D35A0]">{profile?.full_name}</p>
              <p className="text-sm text-slate-500">{profile?.designation} — {profile?.department?.split(',').join(', ')}</p>
              <p className="text-xs text-slate-400">{profile?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Contact */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1D35A0]">
              <Phone className="w-4 h-4" /> Contact
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={set('phone')} placeholder="Phone number" data-testid="profile-phone" />
            </div>
          </CardContent>
        </Card>

        {/* Address with same-as checkbox */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1D35A0]">
              <MapPin className="w-4 h-4" /> Address
            </div>
            <div>
              <Label className="text-xs">Current Address</Label>
              <Input value={form.mumbai_address} onChange={set('mumbai_address')} placeholder="Current address" data-testid="profile-mumbai-address" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" onClick={toggleSameAddress} data-testid="same-address-toggle">
              {sameAddress
                ? <CheckSquare className="w-4 h-4 text-[#1D35A0]" />
                : <Square className="w-4 h-4 text-slate-400" />
              }
              <span className="text-xs text-slate-600">Same as current address</span>
            </label>
            <div>
              <Label className="text-xs">Permanent Address</Label>
              <Input
                value={form.permanent_address}
                onChange={set('permanent_address')}
                placeholder="Permanent address"
                disabled={sameAddress}
                className={sameAddress ? 'bg-slate-50 text-slate-400' : ''}
                data-testid="profile-permanent-address"
              />
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1D35A0]">
              <Shield className="w-4 h-4" /> Emergency Contact
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.emergency_contact_name} onChange={set('emergency_contact_name')} placeholder="Contact name" data-testid="profile-emergency-name" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} placeholder="Contact phone" data-testid="profile-emergency-phone" />
            </div>
            <div>
              <Label className="text-xs">Relation</Label>
              <Input value={form.emergency_contact_relation} onChange={set('emergency_contact_relation')} placeholder="e.g., Spouse, Parent" data-testid="profile-emergency-relation" />
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1D35A0]">
              <FileText className="w-4 h-4" /> Documents
            </div>
            <div>
              <Label className="text-xs">Aadhaar Card Number</Label>
              <Input value={form.aadhaar_card} onChange={set('aadhaar_card')} placeholder="XXXX XXXX XXXX" data-testid="profile-aadhaar" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Aadhaar Front</Label>
                {getUrl(form.aadhaar_front) ? (
                  <div className="relative group mt-1">
                    <img src={getUrl(form.aadhaar_front)} alt="Front" className="w-full h-20 object-cover rounded border cursor-pointer" onClick={() => setViewerImage({ src: getUrl(form.aadhaar_front), alt: 'Aadhaar Card - Front' })} />
                    <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => setViewerImage({ src: getUrl(form.aadhaar_front), alt: 'Aadhaar Card - Front' })} className="p-1.5 bg-white/90 rounded-full hover:bg-white"><FileText className="w-3.5 h-3.5 text-slate-700" /></button>
                    </div>
                  </div>
                ) : (
                  <UploadChooser onFile={(file) => handleFileUpload(file, 'aadhaar_front')} testId="upload-aadhaar-front">
                    <div className="mt-1 w-full h-20 border-2 border-dashed border-slate-200 rounded flex flex-col items-center justify-center text-slate-400 hover:border-[#1D35A0] hover:text-[#1D35A0] transition-colors cursor-pointer">
                      <Upload className="w-4 h-4" />
                      <span className="text-[10px] mt-0.5">Upload Front</span>
                    </div>
                  </UploadChooser>
                )}
                <input ref={aadhaarFrontRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'aadhaar_front')} />
              </div>
              <div>
                <Label className="text-xs">Aadhaar Back</Label>
                {getUrl(form.aadhaar_back) ? (
                  <div className="relative group mt-1">
                    <img src={getUrl(form.aadhaar_back)} alt="Back" className="w-full h-20 object-cover rounded border cursor-pointer" onClick={() => setViewerImage({ src: getUrl(form.aadhaar_back), alt: 'Aadhaar Card - Back' })} />
                    <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => setViewerImage({ src: getUrl(form.aadhaar_back), alt: 'Aadhaar Card - Back' })} className="p-1.5 bg-white/90 rounded-full hover:bg-white"><FileText className="w-3.5 h-3.5 text-slate-700" /></button>
                    </div>
                  </div>
                ) : (
                  <UploadChooser onFile={(file) => handleFileUpload(file, 'aadhaar_back')} testId="upload-aadhaar-back">
                    <div className="mt-1 w-full h-20 border-2 border-dashed border-slate-200 rounded flex flex-col items-center justify-center text-slate-400 hover:border-[#1D35A0] hover:text-[#1D35A0] transition-colors cursor-pointer">
                      <Upload className="w-4 h-4" />
                      <span className="text-[10px] mt-0.5">Upload Back</span>
                    </div>
                  </UploadChooser>
                )}
                <input ref={aadhaarBackRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'aadhaar_back')} />
              </div>
            </div>
            <div>
              <Label className="text-xs">PAN Card Number</Label>
              <Input value={form.pan_card} onChange={set('pan_card')} placeholder="ABCDE1234F" data-testid="profile-pan" />
            </div>
            <div>
              <Label className="text-xs">PAN Card Upload</Label>
              {getUrl(form.pan_url) ? (
                <div className="relative group mt-1">
                  <img src={getUrl(form.pan_url)} alt="PAN" className="w-full h-20 object-cover rounded border cursor-pointer" onClick={() => setViewerImage({ src: getUrl(form.pan_url), alt: 'PAN Card' })} />
                  <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => setViewerImage({ src: getUrl(form.pan_url), alt: 'PAN Card' })} className="p-1.5 bg-white/90 rounded-full hover:bg-white"><FileText className="w-3.5 h-3.5 text-slate-700" /></button>
                  </div>
                </div>
              ) : (
                <UploadChooser onFile={(file) => handleFileUpload(file, 'pan_url')} testId="upload-pan">
                  <div className="mt-1 w-full h-20 border-2 border-dashed border-slate-200 rounded flex flex-col items-center justify-center text-slate-400 hover:border-[#1D35A0] hover:text-[#1D35A0] transition-colors cursor-pointer">
                    <Upload className="w-4 h-4" />
                    <span className="text-[10px] mt-0.5">Upload PAN</span>
                  </div>
                </UploadChooser>
              )}
              <input ref={panRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'pan_url')} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={saving} className="bg-[#1D35A0] hover:bg-[#152A80]" data-testid="profile-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save Changes
        </Button>
        {message && <span className={`text-sm ${message.includes('fail') || message.includes('Fail') ? 'text-red-600' : 'text-green-600'}`}>{message}</span>}
      </div>

      {/* Image Viewer Lightbox */}
      {viewerImage && (
        <ImageViewer src={viewerImage.src} alt={viewerImage.alt} onClose={() => setViewerImage(null)} />
      )}
    </div>
  );
};

export default MyProfile;
